// C:\Users\cornn\source\repos\purehealthnexus\src\components\nutrition\DateRangePicker.jsx
import React, { useMemo, useState } from "react";
import "./nutritioncss/DateRangePicker.css";

const atStart = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const sameDay = (a, b) => a && b && atStart(a).getTime() === atStart(b).getTime();
const inSpan = (d, s, e) => s && e && atStart(d) >= atStart(s) && atStart(d) <= atStart(e);

const fmtRange = (start, end, locale) => {
  if (!start || !end) return "";
  const fmt = new Intl.DateTimeFormat(locale || (typeof navigator !== "undefined" ? navigator.language : "en-GB"), {
    day: "2-digit", month: "short", year: "numeric"
  });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
};

const buildQuickRanges = (today) => {
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const prevM = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  return {
    today:     { label: "Today",         start: today,            end: today },
    "7d":      { label: "Last 7 Days",   start: addDays(today, -6),  end: today },
    "30d":     { label: "Last 30 Days",  start: addDays(today, -29), end: today },
    month:     { label: "This Month",    start: startOfMonth,      end: endOfMonth },
    lastMonth: { label: "Last Month",    start: prevM,             end: prevMEnd },
  };
};

export function getRangeLabel(rangeKey, startDate, endDate, locale) {
  const today = atStart(new Date());
  const ranges = buildQuickRanges(today);
  if (rangeKey && ranges[rangeKey]) return ranges[rangeKey].label;
  return fmtRange(startDate, endDate, locale);
}

const startOfWeekMonday = (d) => {
  const day = atStart(d).getDay();
  const offset = (day + 6) % 7;
  return addDays(atStart(d), -offset);
};

const monthGrid = (month) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = startOfWeekMonday(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

const weekdayLabels = (locale) => {
  const mondayAnchor = new Date(2020, 5, 1);
  return Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: "short" }).format(addDays(mondayAnchor, i))
  );
};

function CalendarGrid({ month, isFirst, startDate, endDate, onMove, onPick, locale, disableFuture }) {
  const today = atStart(new Date());
  const cells = useMemo(() => monthGrid(month), [month]);
  const monthLabel = new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format(month);

  return (
    <div className="drp-calendar">
      <div className="drp-calendar-header">
        {isFirst && <button className="drp-nav-btn" onClick={() => onMove(-1)} aria-label="Previous month">‹</button>}
        <span className="drp-month-label">{monthLabel}</span>
        {!isFirst && <button className="drp-nav-btn" onClick={() => onMove(1)} aria-label="Next month">›</button>}
      </div>

      <div className="drp-weekdays" aria-hidden="true">
        {weekdayLabels(locale).map((d) => <div key={d} className="drp-weekday">{d}</div>)}
      </div>

      <div className="drp-days" role="grid" aria-label={`Dates in ${monthLabel}`}>
        {cells.map((date, i) => {
          const isCurrentMonth = date.getMonth() === month.getMonth();
          const isDisabled = disableFuture && date > today;
          const edge = sameDay(date, startDate) || sameDay(date, endDate);
          const span = inSpan(date, startDate, endDate) && !edge;
          return (
            <button
              key={i}
              className={`drp-day ${!isCurrentMonth ? "drp-day-other" : ""} ${edge ? "drp-day-selected" : ""} ${span ? "drp-day-in-range" : ""}`}
              onClick={() => isCurrentMonth && !isDisabled && onPick(date)}
              disabled={!isCurrentMonth || isDisabled}
              aria-pressed={edge}
              aria-label={date.toDateString()}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Main picker */
export default function DateRangePicker({
  isOpen,
  onClose,
  onApply,
  initialRange = "today",
  initialStart,
  initialEnd,
  locale = (typeof navigator !== "undefined" ? navigator.language : "en-GB"),
  disableFuture = true,
}) {
  const today = useMemo(() => atStart(new Date()), []); // stable "today" for this mount

  // Dynamic quick ranges
  const ranges = useMemo(() => buildQuickRanges(today), [today]);

  // State
  const initRangeKey = ranges[initialRange] ? initialRange : "today";
  const init = ranges[initRangeKey];
  const [selectedKey, setSelectedKey] = useState(initRangeKey);
  const [startDate, setStartDate] = useState(atStart(initialStart || init.start));
  const [endDate, setEndDate] = useState(atStart(initialEnd || init.end));
  const [viewA, setViewA] = useState(new Date(startDate.getFullYear(), startDate.getMonth(), 1));
  const [viewB, setViewB] = useState(addMonths(viewA, 1));

  const pickDay = (date) => {
    const d = atStart(date);
    if (!startDate || (startDate && endDate)) { setSelectedKey("custom"); setStartDate(d); setEndDate(null); }
    else if (d < startDate) { setSelectedKey("custom"); setEndDate(startDate); setStartDate(d); }
    else { setSelectedKey("custom"); setEndDate(d); }
  };

  const applyRange = (key) => {
    const r = ranges[key]; if (!r) return;
    setSelectedKey(key); setStartDate(r.start); setEndDate(r.end);
    const first = new Date(r.start.getFullYear(), r.start.getMonth(), 1);
    setViewA(first); setViewB(addMonths(first, 1));
  };

  const move = (delta) => { const nextA = addMonths(viewA, delta); setViewA(nextA); setViewB(addMonths(nextA, 1)); };

  const display = useMemo(() => {
    if (!startDate || !endDate) return "";
    const fmt = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" });
    return `${fmt.format(startDate)} - ${fmt.format(endDate)}`;
  }, [startDate, endDate, locale]);

  const handleApply = () => { if (startDate && endDate) onApply({ range: selectedKey, startDate, endDate }); onClose(); };

  if (!isOpen) return null;

  return (
    <>
      <div className="drp-backdrop" onClick={onClose} />
      <div className="drp-modal" role="dialog" aria-modal="true" aria-label="Date range picker">
        <div className="drp-header">
          <input className="drp-range-display" value={display} readOnly placeholder="Select date range" />
          <button className="drp-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="drp-content">
          {/* Quick ranges */}
          <div className="drp-sidebar" role="tablist" aria-label="Quick ranges">
            {Object.entries(ranges).map(([key, { label }]) => (
              <button
                key={key}
                className={`drp-quick-btn ${selectedKey === key ? "active" : ""}`}
                onClick={() => applyRange(key)}
                role="tab"
                aria-selected={selectedKey === key}
              >
                {label}
              </button>
            ))}
            <button
              className={`drp-quick-btn ${selectedKey === "custom" ? "active" : ""}`}
              onClick={() => setSelectedKey("custom")}
              role="tab"
              aria-selected={selectedKey === "custom"}
            >
              Custom
            </button>
          </div>

          {/* Calendars */}
          <div className="drp-calendars">
            <CalendarGrid
              month={viewA}
              isFirst
              startDate={startDate}
              endDate={endDate}
              onMove={move}
              onPick={pickDay}
              locale={locale}
              disableFuture={disableFuture}
            />
            <CalendarGrid
              month={viewB}
              startDate={startDate}
              endDate={endDate}
              onMove={move}
              onPick={pickDay}
              locale={locale}
              disableFuture={disableFuture}
            />
          </div>
        </div>

        <div className="drp-footer">
          <button className="drp-btn drp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="drp-btn drp-btn-apply" onClick={handleApply} disabled={!startDate || !endDate}>Apply</button>
        </div>
      </div>
    </>
  );
}
